import {
  Dialog,
  CheckboxField,
  SubmissionForm,
  TextInputField,
  Heading,
  Stack
} from "@bps/fluent-ui";
import { FC } from "react";
import { useBatchAddNotification } from "@libs/api/gateways/plt-notifications/plt-notifications-gateway.hooks";
import {
  AddNotificationDto,
  NotificationPriority,
  NotificationType
} from "@libs/api/gateways/plt-notifications/plt-notifications-gateway.dtos";
import { Validator } from "@bps/validator";

interface SendNotificationFormValues {
  title: string;
  description?: string;
  highPriority: boolean;
}

interface SendNotificationsDialogProps {
  tenantId: string;
  usersIds: string[];
  onDismiss: () => void;
}

const sendNotificationValidator = new Validator<SendNotificationFormValues>(
  validator => ({
    title: validator.string().required()
  })
);

export const SendNotificationsDialog: FC<SendNotificationsDialogProps> = ({
  tenantId,
  usersIds,
  onDismiss
}) => {
  const { mutateAsync } = useBatchAddNotification();

  const onSubmit = async (values: SendNotificationFormValues) => {
    if (!usersIds.length) return;

    const dtos: AddNotificationDto[] = usersIds.map(userId => {
      const notification: AddNotificationDto = {
        tenantId,
        userId,
        title: values.title,
        description: values.description,
        type: NotificationType.System,
        priority: values.highPriority
          ? NotificationPriority.High
          : NotificationPriority.Normal
      };

      return notification;
    });

    await mutateAsync(dtos);
    onDismiss();
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{ showCloseButton: true }}
    >
      <SubmissionForm<SendNotificationFormValues>
        initialValues={{
          title: "",
          description: "",
          highPriority: false
        }}
        onSubmit={onSubmit}
        cancelButtonProps={{ onClick: onDismiss }}
        validate={sendNotificationValidator.validate}
      >
        {() => (
          <>
            <Heading variant="section-heading">
              Send notification to {usersIds.length} users
            </Heading>
            <Stack tokens={{ childrenGap: 8 }}>
              <TextInputField label="Title" name="title" required />
              <TextInputField label="Description" name="description" />
              <CheckboxField label="High priority" name="highPriority" />
            </Stack>
          </>
        )}
      </SubmissionForm>
    </Dialog>
  );
};
